// public-reader/components/ShareSheet.tsx — share action sheet for the
// public reader.
//
// Uses navigator.share() when present (mobile + modern desktop browsers)
// to open the native OS share sheet. Otherwise renders an inline copy-link
// + Twitter / Bluesky / Reddit shortcut row. Closes on outside click and
// on Esc.

import { useCallback, useEffect, useState, type CSSProperties } from "react";

export interface ShareSheetProps {
  shareUrl: string;
  title: string;
  authorName: string;
  scenarioName: string;
  accent: string;
  accentMuted: string;
  ink: string;
  inkMuted: string;
  paper: string;
  paperEdge: string;
  onClose: () => void;
}

function buildShareText(title: string, scenarioName: string): string {
  return `"${title}" — a serialised ${scenarioName} adventure on Ember Hollow.`;
}

interface SocialTarget {
  label: string;
  href: (url: string, text: string) => string;
}

const SOCIAL_TARGETS: SocialTarget[] = [
  {
    label: "Twitter / X",
    href: (url, text) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
  {
    label: "Bluesky",
    href: (url, text) =>
      `https://bsky.app/intent/compose?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
  {
    label: "Reddit",
    href: (url, text) =>
      `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`,
  },
];

export default function ShareSheet(props: ShareSheetProps): JSX.Element {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const shareText = buildShareText(props.title, props.scenarioName);

  // Esc to close.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [props]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(props.shareUrl);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1800);
    } catch {
      setCopyState("failed");
      window.setTimeout(() => setCopyState("idle"), 2400);
    }
  }, [props.shareUrl]);

  const handleNativeShare = useCallback(async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: props.title,
        text: shareText,
        url: props.shareUrl,
      });
    } catch {
      // User dismissed — ignore.
    }
  }, [props.shareUrl, props.title, shareText]);

  // Styles
  const backdrop: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 90,
    padding: 16,
  };
  const card: CSSProperties = {
    maxWidth: 420,
    width: "100%",
    background: props.paper,
    border: `1px solid ${props.paperEdge}`,
    borderRadius: 4,
    padding: 24,
    color: props.ink,
    boxShadow: "0 12px 60px rgba(0,0,0,0.55)",
  };
  const header: CSSProperties = {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    marginBottom: 12,
  };
  const heading: CSSProperties = {
    margin: 0,
    fontSize: "1.15rem",
    fontFamily: "inherit",
    color: props.accent,
    fontWeight: 500,
  };
  const closeBtn: CSSProperties = {
    background: "transparent",
    color: props.inkMuted,
    border: "none",
    cursor: "pointer",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.85rem",
    padding: 4,
  };
  const subtitle: CSSProperties = {
    margin: "0 0 18px",
    color: props.inkMuted,
    fontStyle: "italic",
    fontSize: "0.85rem",
  };
  const urlBox: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    background: "rgba(0,0,0,0.32)",
    border: `1px solid ${props.paperEdge}`,
    borderRadius: 2,
    marginBottom: 14,
  };
  const urlText: CSSProperties = {
    flex: 1,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    color: props.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  const copyBtn: CSSProperties = {
    background: "transparent",
    color: props.accent,
    border: `1px solid ${props.accentMuted}`,
    padding: "6px 12px",
    fontSize: "0.78rem",
    cursor: "pointer",
    borderRadius: 2,
    fontFamily: "inherit",
  };
  const row: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 8,
    marginBottom: 12,
  };
  const socialBtn: CSSProperties = {
    display: "block",
    textAlign: "center",
    padding: "8px 6px",
    border: `1px solid ${props.paperEdge}`,
    background: "transparent",
    color: props.ink,
    textDecoration: "none",
    fontFamily: "inherit",
    fontSize: "0.82rem",
    borderRadius: 2,
  };
  const nativeBtn: CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    background: "transparent",
    color: props.accent,
    border: `1px solid ${props.accentMuted}`,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.92rem",
    borderRadius: 2,
    marginBottom: 8,
  };

  return (
    <div
      style={backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div style={card}>
        <div style={header}>
          <h2 style={heading}>Share this book</h2>
          <button
            type="button"
            style={closeBtn}
            onClick={props.onClose}
            aria-label="Close share sheet"
          >
            ✕
          </button>
        </div>
        <p style={subtitle}>by {props.authorName} · {props.scenarioName}</p>

        <div style={urlBox}>
          <span style={urlText}>{props.shareUrl}</span>
          <button type="button" style={copyBtn} onClick={handleCopy}>
            {copyState === "copied"
              ? "Copied"
              : copyState === "failed"
                ? "Copy failed"
                : "Copy link"}
          </button>
        </div>

        {typeof navigator !== "undefined" && "share" in navigator && (
          <button type="button" style={nativeBtn} onClick={handleNativeShare}>
            Open share sheet…
          </button>
        )}

        <div style={row}>
          {SOCIAL_TARGETS.map((target) => (
            <a
              key={target.label}
              href={target.href(props.shareUrl, shareText)}
              target="_blank"
              rel="noopener noreferrer"
              style={socialBtn}
            >
              {target.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
